// ─────────────────────────────────────────────────────────────────────────────
// CloudSync — player identity + Supabase collection persistence
//
// HOW IT WORKS:
//   1. Every player gets a permanent UUID on first launch (stored in localStorage)
//   2. When a lootbox is opened, the full collection is upserted to Supabase
//   3. The collection lives at: player_collections table, keyed by player_id
//   4. Pazurumon TCG Online will fetch from the same table using the player_id
//
// SETUP:
//   Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env
//   Run the SQL from docs/supabase_setup.sql in the Supabase SQL editor
//
// OFFLINE:
//   If Supabase is not configured or the request fails, the game works
//   normally — cards stay in localStorage. Sync is best-effort, not blocking.
// ─────────────────────────────────────────────────────────────────────────────

const PLAYER_ID_KEY  = 'pazurumon_player_id';
const SUPABASE_URL   = import.meta.env.VITE_SUPABASE_URL   || null;
const SUPABASE_KEY   = import.meta.env.VITE_SUPABASE_ANON_KEY || null;
const TABLE          = 'player_collections';

// ── Supabase REST helper ──────────────────────────────────────────────────────

function supabaseHeaders() {
  return {
    'Content-Type':  'application/json',
    'apikey':        SUPABASE_KEY,
    'Authorization': `Bearer ${SUPABASE_KEY}`,
  };
}

function isConfigured() {
  return !!(SUPABASE_URL && SUPABASE_KEY &&
    SUPABASE_URL !== 'PLACEHOLDER_URL' &&
    SUPABASE_KEY !== 'PLACEHOLDER_ANON_KEY');
}

// ── UUID ──────────────────────────────────────────────────────────────────────

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

export const CloudSync = {

  // ── Player Identity ──────────────────────────────────────────────────────

  /** Returns the player's permanent UUID, generating one on first call. */
  getPlayerId() {
    let id = localStorage.getItem(PLAYER_ID_KEY);
    if (!id) {
      id = generateUUID();
      localStorage.setItem(PLAYER_ID_KEY, id);
    }
    return id;
  },

  /** Short 8-char display version of the player ID. */
  getShortId() {
    return this.getPlayerId().slice(0, 8).toUpperCase();
  },

  /** True if Supabase is configured in .env */
  isConfigured() {
    return isConfigured();
  },

  // ── Cloud Sync ───────────────────────────────────────────────────────────

  /**
   * Upsert the player's full collection to Supabase.
   * Called automatically after every lootbox opening.
   * Fire-and-forget — never blocks gameplay.
   *
   * @param {object} collection - result of Collection.load()
   * @returns {Promise<{ success: boolean, reason?: string }>}
   */
  async syncCollection(collection) {
    if (!isConfigured()) {
      return { success: false, reason: 'supabase_not_configured' };
    }

    const payload = {
      player_id:  this.getPlayerId(),
      cards:      collection.cards     || [],
      fragments:  collection.fragments || {},
      card_count: (collection.cards    || []).length,
      updated_at: new Date().toISOString(),
    };

    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/${TABLE}`,
        {
          method:  'POST',
          headers: {
            ...supabaseHeaders(),
            // Supabase upsert: insert or update on player_id conflict
            'Prefer': 'resolution=merge-duplicates,return=minimal',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const err = await res.text().catch(() => res.status);
        console.warn('[CloudSync] sync failed:', err);
        return { success: false, reason: String(err) };
      }

      return { success: true };
    } catch (e) {
      console.warn('[CloudSync] network error:', e.message);
      return { success: false, reason: 'network_error' };
    }
  },

  /**
   * Fetch a player's collection from Supabase by player_id.
   * Used by Pazurumon TCG Online to import cards.
   *
   * @param {string} [playerId] - defaults to this device's player_id
   */
  async fetchCollection(playerId) {
    if (!isConfigured()) return null;

    const pid = playerId || this.getPlayerId();

    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/${TABLE}?player_id=eq.${encodeURIComponent(pid)}&select=*`,
        { headers: supabaseHeaders() }
      );
      if (!res.ok) return null;
      const rows = await res.json();
      return rows[0] || null;
    } catch {
      return null;
    }
  },

  // ── TCG Bridge — Collection Export ───────────────────────────────────────

  /**
   * Encode the player's collection as a portable base64 code.
   * This code can be pasted into Pazurumon TCG Online to import cards
   * even before a backend account system exists.
   *
   * Format: base64( JSON { v, id, cards, count, ts } )
   */
  exportCollectionCode(collection) {
    const payload = {
      v:     1,
      id:    this.getPlayerId(),
      cards: collection.cards  || [],
      count: (collection.cards || []).length,
      ts:    Date.now(),
    };
    return btoa(JSON.stringify(payload));
  },

  /** Decode and validate a collection code. Returns null if invalid. */
  decodeCollectionCode(code) {
    try {
      const data = JSON.parse(atob(code));
      if (data.v !== 1 || !data.id || !Array.isArray(data.cards)) return null;
      return data;
    } catch {
      return null;
    }
  },

  /**
   * Deep-link URL for auto-importing into TCG Online.
   * Share this link to carry the collection to the next game.
   */
  getImportUrl(collection) {
    const code    = this.exportCollectionCode(collection);
    const tcgBase = import.meta.env.VITE_TCG_GAME_URL || 'https://tcg.pazurumon.com';
    return `${tcgBase}/import?pid=${encodeURIComponent(this.getPlayerId())}&code=${encodeURIComponent(code)}`;
  },
};
